import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import NotAvailable from '../Components/NotAvailable';
import { UNLOCK_STATUS_LABEL, UNLOCK_MODE_LABEL, unlockStuckReason } from '../utils/loyalty';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Unlock-with-points requests (somo-circle1-api#791/#792), for support
// (somo-circle1-api-admin#199 Phase 2). Read-only: an unlock needs no
// fulfilment. "Stuck" is the same list as the API's
// scripts/list-stuck-unlocks.js — points spent (or possibly spent) and the
// unlock not finishing on its own. Support finishes those in Stripe, or gives
// the points back with scripts/cancel-unlock.js.

const STATUS_BADGE = {
    pending: 'badge-secondary',
    processing: 'badge-info',
    awaiting_payment: 'badge-warning',
    completed: 'badge-success',
    failed: 'badge-warning',
    needs_support: 'badge-danger',
};

const formatDollars = (amount) => (amount == null ? '—' : `$${Number(amount).toFixed(2)}`);
const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

// The member's name, or their email when they have no name, so the link to
// the user page always has text.
const memberLabel = (m) => [m.name, m.lastName].filter(Boolean).join(' ') || m.email || 'Unnamed member';

// dollars / cardDollars are the planned split, fixed at the first attempt,
// not money received (PR #214 review). Points count as spent only once
// pointsSpentAt is set (a cancel resets it), and the card part is paid only
// when the unlock completed.
export function amountParts(u) {
    const parts = [{
        key: 'points',
        text: `${formatDollars(u.dollars)} in points`,
        note: u.pointsSpentAt
            ? `spent${u.pointsSpent != null ? ` (${u.pointsSpent} pts)` : ''}`
            : 'not spent',
    }];
    if (u.cardDollars != null) {
        parts.push({
            key: 'card',
            text: `${formatDollars(u.cardDollars)} by card`,
            note: u.status === 'completed' ? 'paid' : 'not paid',
        });
    }
    return parts;
}

function LoyaltyUnlocks() {
    const [unlocks, setUnlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [stuckRules, setStuckRules] = useState(null);
    const [page, setPage] = useState(1);
    const [view, setView] = useState('stuck');
    const [expanded, setExpanded] = useState(null);
    const [allowed, setAllowed] = useState(null); // null = still checking
    // A failed load shows an error, never the empty state: "No stuck unlocks"
    // would tell support there's nothing to do (PR #214 review).
    const [loadError, setLoadError] = useState(null);
    const latestRef = useRef(0);

    const fetchUnlocks = useCallback(async (targetPage, targetView) => {
        const requestId = ++latestRef.current;
        try {
            setLoading(true);
            const params = new URLSearchParams({ page: targetPage, limit: 20, view: targetView });
            const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/unlocks?${params}`);
            const data = await res.json().catch(() => null);
            if (requestId !== latestRef.current) return;
            if (data?.status === 'success') {
                setUnlocks(data.data.unlocks || []);
                setPagination(data.data.pagination || {});
                setStuckRules(data.data.stuckRules || null);
                setPage(targetPage);
                setExpanded(null);
                setLoadError(null);
            } else {
                const message = data?.message || `Failed to load unlocks (HTTP ${res.status}).`;
                setUnlocks([]);
                setLoadError(message);
                toast.error(message);
            }
        } catch (err) {
            if (requestId !== latestRef.current) return;
            console.error('Error fetching unlocks:', err);
            setUnlocks([]);
            setLoadError('Error loading unlocks.');
            toast.error('Error loading unlocks.');
        } finally {
            if (requestId === latestRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        let cancelled = false;
        (async () => {
            // The API gate (requireNonMmlAdmin) is the real control; this only
            // keeps an MML admin off a screen that would 403.
            const currentUser = await authManager.validateUser().catch(() => null);
            if (cancelled) return;
            if (!isNonMmlAdmin(currentUser)) {
                setAllowed(false);
                setLoading(false);
                return;
            }
            setAllowed(true);
            fetchUnlocks(1, 'stuck');
        })();
        return () => {
            cancelled = true;
            latestRef.current += 1;
        };
    }, [fetchUnlocks]);

    const handleViewChange = (e) => {
        setView(e.target.value);
        fetchUnlocks(1, e.target.value);
    };

    if (allowed === false) return <NotAvailable />;

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Membership Unlocks</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row align-items-end">
                                <div className="col-md-3">
                                    <label htmlFor="unlockView">Show</label>
                                    <select id="unlockView" className="form-control" value={view} onChange={handleViewChange}>
                                        <option value="stuck">Stuck (needs support)</option>
                                        <option value="all">All</option>
                                        {Object.entries(UNLOCK_STATUS_LABEL).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-9">
                                    {view === 'stuck' && stuckRules && (
                                        <small className="text-muted">
                                            Points spent (or a spend never confirmed) and not finished after
                                            {' '}{stuckRules.olderThanMinutes} minutes, waiting on a card payment for over
                                            {' '}{stuckRules.awaitingDays} days, or handed to support. Finish it in Stripe, or give the
                                            {' '}points back with <code>scripts/cancel-unlock.js</code>. A spend that was never
                                            {' '}confirmed settles on the member's retry, or with <code>cancel-unlock.js</code>.
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                {loadError && !loading ? (
                                    <div className="alert alert-danger" role="alert" data-testid="unlocks-load-error">
                                        Couldn't load unlocks: {loadError}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger ml-2"
                                            onClick={() => fetchUnlocks(1, view)}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : unlocks.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th>Member</th>
                                                    <th style={{ width: '130px' }}>How</th>
                                                    <th style={{ width: '200px' }}>Amount</th>
                                                    <th style={{ width: '220px' }}>Status</th>
                                                    <th style={{ width: '170px' }}>Last update</th>
                                                    <th style={{ width: '90px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {unlocks.map((u) => (
                                                    <React.Fragment key={u._id}>
                                                        <tr>
                                                            <td>
                                                                {u.userId?._id ? (
                                                                    <Link to={`/users/${u.userId._id}`} className="fwt_600">
                                                                        {memberLabel(u.userId)}
                                                                    </Link>
                                                                ) : <span className="text-muted">Unknown member</span>}
                                                                {u.userId?.email && (
                                                                    <>
                                                                        <br />
                                                                        <small className="text-muted">{u.userId.email}</small>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td>{UNLOCK_MODE_LABEL[u.mode] || u.mode}</td>
                                                            <td>
                                                                {amountParts(u).map((part) => (
                                                                    <div key={part.key}>
                                                                        {part.text}
                                                                        <small className="text-muted"> · {part.note}</small>
                                                                    </div>
                                                                ))}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${STATUS_BADGE[u.status] || 'badge-secondary'}`}>
                                                                    {UNLOCK_STATUS_LABEL[u.status] || u.status}
                                                                </span>
                                                                {u.stuck && (
                                                                    <>
                                                                        {' '}
                                                                        <span className="badge badge-danger">Stuck</span>
                                                                        <br />
                                                                        <small className="text-danger">{unlockStuckReason(u)}</small>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td className="f_sz13">{formatDateTime(u.updatedAt)}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    aria-expanded={expanded === u._id}
                                                                    onClick={() => setExpanded(expanded === u._id ? null : u._id)}
                                                                >
                                                                    {expanded === u._id ? 'Hide' : 'Details'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {expanded === u._id && (
                                                            <tr>
                                                                <td colSpan={6}>
                                                                    <UnlockDetails unlock={u} />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>

                                        {pagination.totalPages > 1 && (
                                            <nav className="mt-3">
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => fetchUnlocks(page - 1, view)} disabled={page === 1}>
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.totalPages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => fetchUnlocks(i + 1, view)}>
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchUnlocks(page + 1, view)}
                                                            disabled={page === pagination.totalPages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>{view === 'stuck' ? 'No stuck unlocks.' : 'Nothing here right now.'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// The ids and timestamps support needs to find the unlock in Stripe and
// squad-rewards, or to run scripts/cancel-unlock.js.
export function UnlockDetails({ unlock: u }) {
    const rows = [
        ['Unlock request ID', <code>{u._id}</code>],
        ['Attempts', `${u.attempts ?? 0} (${u.attemptsAfterSpend ?? 0} after the points were spent)`],
        ['Cancelled attempts', u.cancelledAttempts || 0],
        ['Points spent at', formatDateTime(u.pointsSpentAt)],
        ['Spend unconfirmed since', u.spendUnknownAt ? formatDateTime(u.spendUnknownAt) : '—'],
        ['Stripe customer', u.stripeCustomerId ? <code>{u.stripeCustomerId}</code> : '—'],
        ['Stripe credit', u.creditTransactionId ? <code>{u.creditTransactionId}</code> : '—'],
        ['Stripe subscription', u.stripeSubscriptionId
            ? <><code>{u.stripeSubscriptionId}</code>{u.subscriptionStatus && ` (${u.subscriptionStatus})`}</>
            : '—'],
        ['Started', formatDateTime(u.createdAt)],
        ['Completed', formatDateTime(u.completedAt)],
        ['Last error', u.errorMessage ? <span className="text-danger">{u.errorMessage}</span> : '—'],
    ];
    return (
        <dl className="row mb-0 f_sz13">
            {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                    <dt className="col-sm-3">{label}</dt>
                    <dd className="col-sm-9">{value}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
}

export default LoyaltyUnlocks;
